import { IsoLocale } from '@izettle/constants';
import ownerStructureHelpSvg from 'src/assets/owner-structure-help.svg';
import { getLimitedPartnershipRoleLabels } from '../../../utils/country/SE/data-helpers';
import { nationalityOptions } from '../../common/nationality-options';
import { RoleFieldName } from '../../common/owner-structure';
import { getPrivacyPolicyLink } from '../../common/privacy-policy-link';
import { step, sequence, render, decision } from '../../steps-builder';
import { template } from '../../template';
import { introScreenTemplate, legalPeoplePreparationScreenTemplate, alternativeOwnersListTemplate, normalizeSignatoryFieldsFunction, initLegalPersonsFunction, overviewPageTemplate, hasUBOTemplate, beneficialOwnersListTemplate, } from './common/steps/limited-company';
import { signatoryNameTemplate, signatoryPhoneNumberTemplate, signatoryNationalityTemplate, signatoryNamePrefilledTemplate, } from './common/steps/signatory';
import { SIGNATORY_ACTION } from './data';
const introScreenFI = introScreenTemplate.overrideDeep(({ translate }) => ({
    applicationVariables: {
        content: {
            items: [
                {
                    icon: 'Person',
                    title: translate('GAP_PREPARATION_SCREEN_ITEM_SIGNATORY_DETAILS_TITLE'),
                },
                {
                    icon: 'People',
                    title: translate('PREPARATION_SCREEN_DECISION_MAKERS_HEADER'),
                    description: translate('SECTION_INTRO_OWNER_STRUCTURE_NAME_SSN_NATIONALITY_ADDRESS_DESCRIPTION'),
                },
                {
                    icon: 'IdentityCard',
                    title: translate('GAP_AUTO_VERIFICATION_PREPARATION_SCREEN_ITEM_VERIFICATION_TITLE'),
                    description: translate('GAP_LTD_PREPARATION_SCREEN_ITEM_VERIFICATION_DESCRIPTION'),
                },
            ],
        },
    },
}));
const legalPeoplePreparationScreenFI = legalPeoplePreparationScreenTemplate.overrideDeep(({ translate }) => ({
    applicationVariables: {
        content: {
            helpTexts: [
                {
                    title: translate('GAP_LEGAL_PERSONS_PREPARATION_SCREEN_HELP_2_ACCORDION_TITLE'),
                    body: translate('GAP_LEGAL_PERSONS_PREPARATION_SCREEN_HELP_2_ACCORDION_CONTENT'),
                },
                {
                    title: translate('HELP_TEXT_INFO_LOCATION_TITLE'),
                    body: translate('BUSINESS_DETAILS_FI_YTJ_HELP_TEXT_INFO_LOCATION_BODY'),
                },
            ],
            items: [
                {
                    icon: 'People',
                    description: translate('GAP_LTD_LEGAL_PEOPLE_PREPARATION_SCREEN_ITEM_DECISION_MAKERS_DESCRIPTION'),
                },
            ],
        },
    },
}));
const personStep = (translate) => ({
    ID: 'personStepId',
    backendValidation: true,
    fields: ['firstName', 'lastName', 'legalEntityNumber', 'nationality', 'streetAddress', 'postalCode', 'city'],
    JSONSchema: {
        type: 'object',
        properties: {
            nameTitle: {
                title: translate('PERSON_NAME_LABEL'),
                type: 'null',
            },
            firstName: {
                type: 'string',
            },
            lastName: {
                type: 'string',
            },
            legalEntityNumberQuestion: {
                title: translate('PERSON_LEGAL_ENTITY_NUMBER_SSN_LABEL'),
                type: 'null',
            },
            legalEntityNumber: {
                type: 'string',
            },
            nationalityTitle: {
                title: translate('PERSON_NATIONALITY_LABEL'),
                type: 'null',
            },
            nationality: {
                type: 'string',
                enum: nationalityOptions.map((option) => option.value),
                enumNames: nationalityOptions.map((option) => translate(option.textKey)),
            },
            addressTitle: {
                title: translate('PERSON_HOME_ADDRESS_LABEL'),
                type: 'null',
            },
            streetAddress: {
                type: 'string',
            },
            postalCode: {
                type: 'string',
            },
            city: {
                type: 'string',
            },
        },
    },
    UISchema: {
        firstName: {
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('FIRST_NAME_LABEL'),
            },
        },
        lastName: {
            'ui:marginBottom': 'medium',
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('LAST_NAME_LABEL'),
            },
        },
        legalEntityNumber: {
            'ui:marginBottom': 'medium',
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:placeholder': translate('PERSON_LEGAL_ENTITY_NUMBER_FI_PLACEHOLDER'),
            'ui:options': {
                mask: "999999\u2C6F999\u0152" /* CopyFI.SOCIAL_SECURITY_NUMBER_MASK */,
            },
        },
        nationality: {
            'ui:marginBottom': 'medium',
            'ui:title': false,
            'ui:widget': 'CustomComboBoxWidget',
            'ui:emptyValue': '',
            'ui:placeholder': translate('NATIONALITY_PLACEHOLDER'),
            'ui:options': {
                sortByLabels: 'asc',
            },
        },
        streetAddress: {
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('ADDRESS_STREET_ADDRESS_WITH_HOUSE_NUMBER_LABEL'),
            },
        },
        postalCode: {
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('ADDRESS_POSTAL_CODE_LABEL'),
            },
        },
        city: {
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('ADDRESS_CITY_LABEL'),
            },
        },
    },
    applicationVariables: {
        stepName: 'newPerson',
    },
});
const alternativeOwnersListFI = alternativeOwnersListTemplate(personStep).overrideDeep(({ translate }) => ({
    applicationVariables: {
        content: {
            helpTexts: [
                {
                    title: translate('HELP_TEXT_INFO_LOCATION_TITLE'),
                    body: translate('BUSINESS_DETAILS_FI_YTJ_HELP_TEXT_INFO_LOCATION_BODY'),
                },
            ],
        },
    },
}));
const beneficialOwnersListFI = beneficialOwnersListTemplate(personStep).overrideDeep(({ translate }) => ({
    applicationVariables: {
        content: {
            helpTexts: [
                {
                    title: translate('HAS_OTHER_LEGAL_PERSONS_LIMITED_COMPANY_HELP_TEXT_HOW_TO_ANSWER_TITLE'),
                    body: translate('HAS_OTHER_LEGAL_PERSONS_LIMITED_COMPANY_HELP_TEXT_HOW_TO_ANSWER_BODY', [
                        ownerStructureHelpSvg.src,
                    ]),
                },
                {
                    title: translate('HELP_TEXT_INFO_LOCATION_TITLE'),
                    body: translate('BUSINESS_DETAILS_FI_YTJ_HELP_TEXT_INFO_LOCATION_BODY'),
                },
            ],
        },
    },
}));
/* eslint-disable sonarjs/no-identical-functions */
const partnersListFI = ({ translate }) => ({
    componentName: 'OwnerList',
    backendValidation: true,
    fields: ['rolePartners'],
    personStep: personStep(translate),
    applicationVariables: {
        stepName: 'partnersList',
        segment: 'ownerStructure',
        roleFieldName: RoleFieldName.rolePartners,
        signatoryRoleLabel: translate('OWNER_STRUCTURE_ROLE_VALUE_SIGNATORY'),
        personRoleLabels: getLimitedPartnershipRoleLabels(translate),
        content: {
            title: translate('OWNER_STRUCTURE_PARTNERS_LABEL'),
            description: translate('OWNER_STRUCTURE_PARTNERS_DESCRIPTION'),
            instruction: translate('OWNER_STRUCTURE_ROLE_SELECT_ALL_INSTRUCTION'),
            helpTexts: [
                {
                    title: translate('HELP_TEXT_INFO_LOCATION_TITLE'),
                    body: translate('BUSINESS_DETAILS_FI_YTJ_HELP_TEXT_INFO_LOCATION_BODY'),
                },
            ],
        },
    },
});
const overviewPageFI = overviewPageTemplate.overrideDeep(({ translate }) => ({
    applicationVariables: {
        version: 'GAP_FI_PARTNERSHIP',
        content: {
            submitInstructions: translate('GAP_OVERVIEW_SUBMIT_INSTRUCTIONS', [getPrivacyPolicyLink(IsoLocale.fiFI)]),
        },
    },
}));
const signatoryLegalEntityNumber = template(({ translate }) => ({
    backendValidation: true,
    fields: ['signatoryLegalEntityNumber'],
    JSONSchema: {
        type: 'object',
        properties: {
            signatoryLegalEntityNumber: {
                title: translate('GAP_SIGNATORY_LEGAL_ENTITY_NUMBER_SSN_LABEL'),
                type: 'string',
            },
        },
    },
    UISchema: {
        signatoryLegalEntityNumber: {
            'ui:emptyValue': '',
            'ui:autofocus': true,
            'ui:placeholder': translate('PERSON_LEGAL_ENTITY_NUMBER_FI_PLACEHOLDER'),
            'ui:options': {
                mask: "999999\u2C6F999\u0152" /* CopyFI.SOCIAL_SECURITY_NUMBER_MASK */,
            },
        },
    },
    applicationVariables: {
        stepName: 'signatoryLegalEntityNumber',
        segment: 'personalDetails',
        content: {
            helpTexts: [
                {
                    title: translate('GAP_SIGNATORY_SSN_HELP_TEXT_WHY_TITLE'),
                    body: translate('GAP_SIGNATORY_SSN_HELP_TEXT_WHY_DESCRIPTION'),
                },
            ],
        },
    },
}));
const signatoryAddressFI = ({ translate }) => ({
    backendValidation: true,
    fields: ['signatoryStreetAddress', 'signatoryPostalCode', 'signatoryCity'],
    JSONSchema: {
        type: 'object',
        properties: {
            signatoryStreetQuestion: {
                title: translate('GAP_SIGNATORY_ADDRESS_LABEL'),
                type: 'null',
            },
            signatoryStreetAddress: {
                type: 'string',
            },
            signatoryPostalCode: {
                type: 'string',
            },
            signatoryCity: {
                type: 'string',
            },
        },
    },
    UISchema: {
        signatoryStreetAddress: {
            'ui:title': false,
            'ui:autofocus': true,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('ADDRESS_STREET_ADDRESS_WITH_HOUSE_NUMBER_LABEL'),
            },
        },
        signatoryPostalCode: {
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('ADDRESS_POSTAL_CODE_LABEL'),
                mask: "99999" /* CopyFI.ADDRESS_POSTAL_CODE_MASK */,
            },
        },
        signatoryCity: {
            'ui:title': false,
            'ui:emptyValue': '',
            'ui:options': {
                label: translate('ADDRESS_CITY_LABEL'),
            },
        },
    },
    applicationVariables: {
        stepName: 'signatoryAddress',
        segment: 'personalDetails',
        content: {
            helpTexts: [
                {
                    title: translate('GAP_SIGNATORY_ADDRESS_HELP_TEXT_WHY_TITLE'),
                    body: translate('GAP_SIGNATORY_ADDRESS_HELP_TEXT_WHY_DESCRIPTION'),
                },
            ],
        },
    },
});
const signatoryPhoneNumberFI = signatoryPhoneNumberTemplate.extendDeep(({ translate }) => ({
    UISchema: {
        signatoryPhoneNumber: {
            'ui:placeholder': translate('FORM_EXAMPLE', ["045 123 45 67" /* CopyFI.SIGNATORY_PHONE_NUMBER_EXAMPLE */]),
        },
    },
}));
/* eslint-disable sonarjs/no-identical-functions */
const hasUBOFI = hasUBOTemplate.overrideDeep(({ translate }) => ({
    applicationVariables: {
        content: {
            helpTexts: [
                {
                    title: translate('HAS_OTHER_LEGAL_PERSONS_LIMITED_COMPANY_HELP_TEXT_HOW_TO_ANSWER_TITLE'),
                    body: translate('HAS_OTHER_LEGAL_PERSONS_LIMITED_COMPANY_HELP_TEXT_HOW_TO_ANSWER_BODY', [
                        ownerStructureHelpSvg.src,
                    ]),
                },
                {
                    title: translate('HELP_TEXT_INFO_LOCATION_TITLE'),
                    body: translate('BUSINESS_DETAILS_FI_YTJ_HELP_TEXT_INFO_LOCATION_BODY'),
                },
            ],
        },
    },
}));
const newSignatorySequence = [
    step(signatoryNameTemplate),
    step(signatoryLegalEntityNumber),
    step(signatoryPhoneNumberFI),
    step(signatoryAddressFI),
    step(signatoryNationalityTemplate),
];
const existingSignatorySequence = [
    step(signatoryAddressFI),
    step(signatoryNationalityTemplate),
];
const rootSequence = sequence([
    step(introScreenFI),
    decision('signatoryAction', signatoryNamePrefilledTemplate, [
        { when: SIGNATORY_ACTION.CHANGE_SIGNATORY, branch: sequence(newSignatorySequence) },
        { when: SIGNATORY_ACTION.SET_NATIONALITY, branch: sequence(existingSignatorySequence) },
    ]),
    step(normalizeSignatoryFieldsFunction),
    step(legalPeoplePreparationScreenFI),
    step(initLegalPersonsFunction),
    step(partnersListFI),
    decision('hasUBO', hasUBOFI, [
        { when: true, branch: step(beneficialOwnersListFI) },
        { when: false, branch: step(alternativeOwnersListFI) },
    ]),
    step(overviewPageFI),
]);
export const GAP_FI_PARTNERSHIP = (context) => render(rootSequence, context);
